import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';
import { usePrevious } from '_hooks';

import Button from 'components/atomic/Button';
import Spinner from 'components/atomic/Spinner';
import BaseField from 'components/atomic/fields/BaseField';
import MaskField from 'components/atomic/fields/MaskField';
import SelectField from 'components/atomic/fields/SelectField';
import Text from 'components/atomic/layouts/Text';

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
`;

const ContactInfoForm = ({ loading, error, submitDisabled, defaultValues, onSubmit }) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    reset,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const prevDefaultValues = usePrevious(defaultValues);

  const [emailVerifying, setEmailVerifying] = useState(false);

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    if (!isEqual(prevDefaultValues, defaultValues)) {
      reset();
    }
  }, [prevDefaultValues, defaultValues, reset]);

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.firstName)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.lastName)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <MaskField {...getFieldProps(FIELD_KEYS.phone)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <BaseField
            {...getFieldProps(FIELD_KEYS.email, {
              validationOptions: { setProcessing: setEmailVerifying },
            })}
            control={control}
            loading={emailVerifying}
            feedback={emailVerifying ? 'Verifying...' : undefined}
            bottomSpace={theme.spaces.regular}
          />
        </Col>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.address1)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.city)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <SelectField
            {...getFieldProps(FIELD_KEYS.state)}
            searchable
            control={control}
            bottomSpace={theme.spaces.regular}
          />
        </Col>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.zip)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={3} />
        <Col xs={12} md={6}>
          <Button type="submit" disabled={submitDisabled} color={theme.colors.gray.primary}>
            Save Information
          </Button>
        </Col>
        <Col xs={12} md={3} />
      </Row>

      {loading && (
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} topSpace={theme.spaces.regular}>
          <StyledMessageContainer>
            <Spinner />
            <div>Updating...</div>
          </StyledMessageContainer>
        </Text>
      )}

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}
    </Form>
  );
};

ContactInfoForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default ContactInfoForm;
